import { useCallback, useEffect, useState } from 'react'
import { ApiError, explain } from '@/shared/api/problem'
import { listUnits, listChildren, EMPTY_UNIT_FILTERS } from '@/features/geography/api'
import type { UnitResponse } from '@/shared/api/types'

/**
 * County → constituency → ward → station, one cascading `<select>` per level.
 *
 * ## The cross-feature grant, taken deliberately
 *
 * `/geography/units` is gated on `geography:read` — a DIFFERENT feature's authority, which
 * an incident reporter need not hold. The failure mode when they do not is a 403 and a
 * silently empty dropdown, and that has cost this codebase a day before.
 *
 * There is no incident-owned unit list to source this from, so the grant is accepted and
 * stated: a deployment that wants the unit picker must grant `geography:read` alongside
 * `incident:report`. What is NOT acceptable is failing quietly, so a refusal here renders
 * the sentence naming the authority, and **step 3 still completes** — "use my location"
 * and "pick on the map" are untouched by it, and so is the explicit "I do not know".
 */

const LEVELS: { type: string; label: string }[] = [
  { type: 'COUNTY', label: 'County' },
  { type: 'CONSTITUENCY', label: 'Constituency' },
  { type: 'WARD', label: 'Ward' },
  { type: 'POLLING_STATION', label: 'Polling station' },
]

export function UnitPicker({
  value, onPick,
}: {
  /** The currently chosen unit id, or null. */
  value: string | null
  onPick: (unit: UnitResponse | null) => void
}) {
  const [counties, setCounties] = useState<UnitResponse[]>([])
  const [children, setChildren] = useState<Record<number, UnitResponse[]>>({})
  const [chosen, setChosen] = useState<(UnitResponse | null)[]>([null, null, null, null])
  const [error, setError] = useState<string | null>(null)
  const [denied, setDenied] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    setLoading(true)
    listUnits({ ...EMPTY_UNIT_FILTERS, type: ['COUNTY'], sort: 'name' }, 0, 100)
      .then((page) => { if (alive) setCounties(page.content) })
      .catch((err) => {
        if (!alive) return
        // A 403 here is not "no counties" — it is a missing authority, and saying which
        // one is the difference between a user who can ask for it and one who cannot.
        if (err instanceof ApiError && err.status === 403) setDenied(true)
        else setError(explain(err))
      })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const choose = useCallback(async (level: number, unit: UnitResponse | null) => {
    const next = chosen.map((c, i) => (i < level ? c : i === level ? unit : null))
    setChosen(next)
    // Deeper levels are stale the moment a shallower one changes.
    setChildren((prev) => {
      const copy = { ...prev }
      for (let i = level + 1; i < LEVELS.length; i++) delete copy[i]
      return copy
    })
    onPick(unit)
    if (!unit || level + 1 >= LEVELS.length) return
    try {
      const kids = await listChildren(unit.id)
      setChildren((prev) => ({ ...prev, [level + 1]: kids }))
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setDenied(true)
      else setError(explain(err))
    }
  }, [chosen, onPick])

  if (denied) {
    return (
      <p className="text-erp-sm text-muted-foreground border-l-2 border-amber-400 pl-3 py-1">
        The unit list needs the <code className="font-mono text-erp-xs">geography:read</code>{' '}
        permission, which this account does not hold. Ask an administrator for it, or place
        the incident with your current location or on the map instead — either records a
        coordinate the desk can work from.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {error && <p className="text-erp-xs text-red-700 dark:text-red-400">{error}</p>}
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        {LEVELS.map((level, i) => {
          const opts = i === 0 ? counties : (children[i] ?? [])
          const disabled = i > 0 && !chosen[i - 1]
          return (
            <label key={level.type} className="min-w-0">
              <span className="block text-erp-xs text-muted-foreground mb-1">{level.label}</span>
              <select
                className="erp-input h-9 w-full"
                disabled={disabled || (i === 0 && loading)}
                value={chosen[i]?.id ?? ''}
                onChange={(e) => {
                  const picked = opts.find((u) => u.id === e.target.value) ?? null
                  void choose(i, picked)
                }}
              >
                <option value="">
                  {i === 0 && loading ? 'Loading…' : disabled ? '—' : `Any ${level.label.toLowerCase()}`}
                </option>
                {opts.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </label>
          )
        })}
      </div>
      {value && (
        <p className="text-erp-xs text-muted-foreground">
          Placed at{' '}
          <strong className="text-foreground">
            {chosen.filter(Boolean).map((c) => c!.name).join(' › ') || 'the selected unit'}
          </strong>
          . Naming a unit is enough on its own — a coordinate is better where you have one.
        </p>
      )}
    </div>
  )
}
